import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useNavigate } from 'react-router-dom'
import { Tooltip } from '@mui/material'
import { toast } from 'components/global/CUK/Toast/Toast'
import {
  Box,
  Stack,
  Typography,
  Snackbar,
  Alert
} from '@mui/material'

import Status from 'components/global/Status'
import CardActionButton from 'components/global/CardActions/CardActionButton'
import CardPreview from 'packages/ui/CardPreview'
import { ResponsiveSideContent } from 'components-shared'
import { useUser } from 'store'
import {
  BlockContent,
  BlockInfo,
  BlockTitle,
  CardMainContent,
  CardUserInfos,
  CodeBox,
  Device,
  DeviceOtherInfos,
  DeviceTopBar,
  RoomName,
  WifiPSK,
} from './CardStyles'
import { ActionsMenu } from './ActionsMenu'
import { useRoom } from '../use-rooms'
import { getDeviceStatus } from '../device-status'


const Card = () => {
  const { t } = useTranslation()
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false)
  const selectedRoom = useRoom()
  const navigate = useNavigate()
  const [user] = useUser()

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return
    }
    setCopySnackbarOpen(false)
  }

  const handleCheckout = () => {
    navigate(`${location.pathname}/checkout${location.search}`)
  }
  const handleClose = () => navigate({ pathname: '..' })

  return (
    <ResponsiveSideContent onClose={handleClose} style={{ width: 420, minHeight: 0 }}>
      <CardPreview
        title={t('stream-cast.card.title')}
        loading={!selectedRoom}
        onClose={handleClose}>
        {selectedRoom && (
          <Stack flex={1} minHeight={0}>
            <CardUserInfos>
              <RoomName>
                {t('stream-cast.card.room')} {selectedRoom.name}
              </RoomName>
              <Tooltip title={t('stream-cast.card.copy_wifi.tooltip')} placement={'top'} arrow>
                <CodeBox
                  onClick={() => {
                    navigator.clipboard.writeText(selectedRoom.wifi_psk)
                    toast.success(t('stream-cast.card.copy_wifi.toast'))
                  }}
                  sx={{ cursor: 'pointer' }}
                >
                  <i className={'fa-light fa-copy'} style={{ fontSize: 12 }} />
                  <WifiPSK>{selectedRoom.wifi_psk}</WifiPSK>
                </CodeBox>
              </Tooltip>
            </CardUserInfos>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
              <CardActionButton
                tooltiptitle={t('stream-cast.actions.checkout')}
                iconlabel="arrow-right-from-bracket"
                onClick={() => {
                  if (!user.demo_mode) {
                    handleCheckout()
                  }
                }}
                sx={{
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  width: '32px',
                  height: '32px'
                }}
              />
            </Box>
            <CardMainContent>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  msOverflowStyle: 'none',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': {
                    display: 'none'
                  }
                }}
              >
                {selectedRoom.devices && selectedRoom.devices.length > 0 ? (
                  selectedRoom.devices.map((device) => {
                    const statusType = getDeviceStatus(device)

                    const borderColors = {
                      operational: '#92BC62',
                      unreachable: '#EEA20A',
                      not_configured: '#EEA20A'
                    }

                    const statusColors = {
                      operational: { color: '#587F31', bg: '#E6F0D7' },
                      unreachable: { color: '#DC2626', bg: '#FEE2E2' },
                      not_configured: { color: '#D97706', bg: '#FEF3C7' }
                    }

                    const statusText = {
                      operational: t('stream-cast.card.devices.up'),
                      unreachable: t('stream-cast.card.devices.down'),
                      not_configured: t('stream-cast.card.devices.not_setup')
                    }

                    return (
                      <Device
                        key={device.uuid}
                        sx={{
                          borderColor: borderColors[statusType],
                          borderWidth: '1px',
                          borderStyle: 'solid',
                          borderRadius: '16px',
                          padding: '16px',
                          mb: 2
                        }}
                      >
                        <DeviceTopBar>
                          <Stack direction="row" justifyContent="space-between" width="100%">
                            <Stack direction={'row'} gap={1} alignItems="center">
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontSize: 14,
                                  fontWeight: 600,
                                  textTransform: 'capitalize'
                                }}
                              >
                                {device.kind}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  textTransform: 'capitalize'
                                }}
                              >
                                {device.manufacturer}
                              </Typography>
                            </Stack>

                            <Status
                              status={statusText[statusType]}
                              color={statusColors[statusType].color}
                              bgcolor={statusColors[statusType].bg}
                              height={24}
                              fontSize={12}
                              isUppercase={false}
                              borderRadius="50px"
                            />
                          </Stack>
                        </DeviceTopBar>
                        <DeviceOtherInfos>
                          <BlockInfo>
                            <BlockTitle sx={{ fontSize: 12, fontWeight: 500 }}>
                              {t('stream-cast.card.devices.serial_number')}
                            </BlockTitle>
                            <BlockContent sx={{ fontSize: 14 }}>{device.serial || '-'}</BlockContent>
                          </BlockInfo>
                          <BlockInfo>
                            <BlockTitle sx={{ fontSize: 12, fontWeight: 500 }}>
                              {t('stream-cast.card.devices.mac_address')}
                            </BlockTitle>
                            <BlockContent sx={{ fontSize: 14 }}>{device.mac || '-'}</BlockContent>
                          </BlockInfo>
                        </DeviceOtherInfos>
                        <Stack mt={.5}>
                          <ActionsMenu
                            name={selectedRoom.name}
                            hotspot_id={selectedRoom.hotspot_id}
                            tvs_pincode={selectedRoom.tvs_pincode}
                            site_ssid={selectedRoom.ssid_name}
                            wifi_psk={selectedRoom.wifi_psk}
                            device={device}
                          />
                        </Stack>
                      </Device>
                    )
                  })
                ) : (
                  <Typography variant="body2" sx={{ textAlign: 'center', py: 2 }}>
                    {t('stream-cast.card.no_devices')}
                  </Typography>
                )}
              </Box>
            </CardMainContent>
          </Stack>
        )}
      </CardPreview>

      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {t('stream-cast.card.wifi_psk_copied')}
        </Alert>
      </Snackbar>

      <Outlet />
    </ResponsiveSideContent>
  )
}

export default Card
